import { t } from "../i18n/index.ts";
import { dismiss, hold, release, type ToastKind, toasts } from "../lib/toast.ts";
import { Link, RemoveButton } from "./Button.tsx";

/** A toast's way on, a link or a button: underlined, after its text. */
// In the Android app (`android:`): a snackbar's action, its words alone.
const WAY_ON =
  "ml-3 font-medium whitespace-nowrap underline decoration-current/50 hover:decoration-current android:font-semibold android:no-underline";

function Region({ kind }: { kind: ToastKind }) {
  const list = toasts.value.filter((x) => x.kind === kind);
  // Errors on the danger fill; the rest on the toast's own (dark on paper, a raised dark one on a dark theme: never
  // the scale's text colour, which a dark theme makes light). A hairline keeps it off a page as dark as it.
  const tone = kind === "error" ? "bg-danger text-on-fill" : "bg-toast text-on-toast";
  return (
    <div role={kind === "error" ? "alert" : "status"} class="flex flex-col gap-2">
      {list.map((x) => (
        // Its time stops while it's read (the pointer on it, the keyboard in it).
        <div
          key={x.id}
          onPointerEnter={() => hold(x.id)}
          onPointerLeave={() => release(x.id)}
          onFocusIn={() => hold(x.id)}
          onFocusOut={() => release(x.id)}
          class={`flex items-start gap-3 rounded-md border border-on-toast/10 px-4 py-3 text-sm shadow-lg android:min-h-12 android:items-center android:rounded-lg android:border-transparent android:shadow-md ${tone}`}
        >
          <span class="min-w-0 flex-1 wrap-anywhere">
            {x.text}
            {x.links?.map((l) =>
              "href" in l ? (
                <Link key={l.label} href={l.href} onClick={() => dismiss(x.id)} class={WAY_ON}>
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  type="button"
                  onClick={() => {
                    dismiss(x.id);
                    l.onClick();
                  }}
                  class={WAY_ON}
                >
                  {l.label}
                </button>
              ),
            )}
          </span>
          <RemoveButton size="md" label={t("common.dismiss")} onClick={() => dismiss(x.id)} />
        </div>
      ))}
    </div>
  );
}

/** Live regions for toasts: polite for info, assertive (role=alert) for errors. */
export function Toaster() {
  return (
    <div class="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-end gap-2 *:pointer-events-auto sm:left-auto sm:w-96">
      <Region kind="info" />
      <Region kind="error" />
    </div>
  );
}
